// Calcul nutritionnel d'une portion (composition stockée pour 100 g) et repères.

import { ATWATER } from './energy';
import { FREE_SUGARS_MAX_PCT } from './health-guidelines';
import { round } from './statistics';
import type { Consumption, FoodComposition, Nutrients } from './types';

// Nutriments absolus pour `grams` grammes d'un aliment.
export function nutrientsFor(food: FoodComposition, grams: number): Nutrients {
  const r = grams / 100;
  return {
    kcal: food.kcal100g * r,
    protein: food.protein100g * r,
    carbs: food.carbs100g * r,
    sugars: food.sugars100g * r,
    fat: food.fat100g * r,
    saturated: food.saturated100g * r,
    fiber: food.fiber100g * r,
    salt: food.salt100g * r,
  };
}

export function consumptionNutrients(c: Consumption): Nutrients {
  return nutrientsFor(c.food, c.grams);
}

// Élément neutre pour les sommes de nutriments.
export function zeroNutrients(): Nutrients {
  return { kcal: 0, protein: 0, carbs: 0, sugars: 0, fat: 0, saturated: 0, fiber: 0, salt: 0 };
}

function addNutrients(a: Nutrients, b: Nutrients): Nutrients {
  return {
    kcal: a.kcal + b.kcal,
    protein: a.protein + b.protein,
    carbs: a.carbs + b.carbs,
    sugars: a.sugars + b.sugars,
    fat: a.fat + b.fat,
    saturated: a.saturated + b.saturated,
    fiber: a.fiber + b.fiber,
    salt: a.salt + b.salt,
  };
}

export function sumNutrients(items: readonly Nutrients[]): Nutrients {
  return items.reduce(addNutrients, zeroNutrients());
}

// Répartition énergétique (% des kcal) par macronutriment via Atwater.
export interface MacroSplit {
  proteinPct: number;
  carbsPct: number;
  fatPct: number;
  energyKcal: number;
}

export function macroSplit(n: Nutrients): MacroSplit {
  const pKcal = n.protein * ATWATER.protein;
  const cKcal = n.carbs * ATWATER.carbs;
  const fKcal = n.fat * ATWATER.fat;
  const total = pKcal + cKcal + fKcal;
  if (total === 0) {
    return { proteinPct: 0, carbsPct: 0, fatPct: 0, energyKcal: 0 };
  }
  return {
    proteinPct: round((pKcal / total) * 100, 1),
    carbsPct: round((cKcal / total) * 100, 1),
    fatPct: round((fKcal / total) * 100, 1),
    energyKcal: round(total),
  };
}

// Protéines par kg de poids corporel réellement consommées (repères par phase :
// health-guidelines.ts, PROTEIN_PER_KG_BY_PHASE).
export function proteinPerKg(proteinGrams: number, bodyWeightKg: number): number {
  if (bodyWeightKg <= 0) return NaN;
  return proteinGrams / bodyWeightKg;
}

// Repères journaliers (adulte, base 2000 kcal). Sel et sucres alignés sur l'OMS ;
// fibres ANSES ; AGS via SAT_FAT_MAX_PCT (≤ 10 %E). Sucres = sucres LIBRES < 10 %E
// (FREE_SUGARS_MAX_PCT) = 10 % × 2000 / 4 kcal·g⁻¹ = 50 g.
export const DAILY_REFERENCE = {
  fiberG: 30, // ANSES
  saltG: 5, // OMS : < 5 g/j
  saturatedG: 20, // ≤ 10 %E (OMS) sur base 2000 kcal : 10 % × 2000 / 9 ≈ 22 g, arrondi prudent
  sugarsG: Math.round(((FREE_SUGARS_MAX_PCT / 100) * 2000) / 4), // sucres libres < 10 %E (OMS) = 50 g
} as const;
